import React from 'react';
import type { FileUploadItem } from '../fileUploader';
import type UploadContexts from './UploadContexts';
import useFileUpload from './useFileUpload';

export type UseFileItemProps = {
  context: UploadContexts;
  file: File | null;
  fileName: string;
  id: string;
  onError: (e: unknown) => void;
  onRemove: () => void;
  onUpload: (item: FileUploadItem) => void;
  showDelete: boolean;
  uploadId: string | null;
};

export default function useFileItem({
  context,
  file,
  fileName,
  id,
  onError,
  onRemove,
  onUpload,
  showDelete,
  uploadId,
}: UseFileItemProps) {
  const isMountedRef = React.useRef(true);
  const [isBusy, setIsBusy] = React.useState(!uploadId);
  const { download, remove, upload } = useFileUpload(context);

  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const handleDownload = React.useCallback(async () => {
    if (!uploadId) return;
    try {
      await download(uploadId, fileName);
    } catch (err: unknown) {
      onError(err);
    }
  }, [uploadId]);

  const handleRemove = React.useCallback(async () => {
    if (!uploadId || isBusy || !showDelete) return;
    setIsBusy(true);
    try {
      await remove(uploadId);
      onRemove();
    } catch (err: unknown) {
      onError(err);
    } finally {
      if (isMountedRef.current) {
        setIsBusy(false);
      }
    }
  }, [isBusy, showDelete, uploadId]);

  React.useEffect(() => {
    if (uploadId || !(file instanceof File)) return;
    (async () => {
      try {
        const res = await upload(file);
        onUpload({
          file,
          fileName,
          id: id || `${fileName}-${Date.now()}`,
          ...res,
        });
      } catch (err) {
        onError(err);
      } finally {
        setIsBusy(false);
      }
    })();
  }, [uploadId]);

  return {
    handleDownload,
    handleRemove,
    isBusy,
  };
}
